const DIRECTIONS = [
  [0, -1],
  [1, 0],
  [0, 1],
  [-1, 0],
];
const SHAPES = [
  [0, 2],
  [0, 1],
  [0, 1, 2],
];
const paths = [
  [2, 7, 6, 11, 12, 17, 22],
  [2, 7, 8, 13, 12, 11, 16, 21, 22],
  [2, 1, 6, 11, 12, 13, 18, 17, 22],
  [2, 3, 8, 7, 6, 11, 12, 13, 18, 17, 16, 21, 22],
  [2, 1, 6, 7, 8, 13, 12, 11, 16, 17, 18, 23, 22],
];
function direction(a, b) {
  const dx = (b % 5) - (a % 5),
    dy = Math.floor(b / 5) - Math.floor(a / 5);
  return DIRECTIONS.findIndex(([x, y]) => x === dx && y === dy);
}
/** Authored connected downhill/sideways channels, scrambled by level and seed.
 * Water is a conserved finite packet; fire evaporates 12 units into a steam gate.
 * Each board has a guaranteed complete route, without a solution in its view. */
export function createWaterworks(seed = 7, level = 1) {
  if (!Number.isInteger(seed) || !Number.isInteger(level) || level < 1 || level > 100)
    throw new RangeError('Invalid water level.');
  const path = paths[(level - 1) % paths.length];
  const cells = Array.from({ length: 25 }, () => ({ shape: 0, rotation: 0, active: false }));
  for (let i = 0; i < path.length; i++) {
    const index = path[i],
      a = i ? direction(index, path[i - 1]) : 0,
      b = i === path.length - 1 ? 2 : direction(index, path[i + 1]);
    const shape = (a + 2) % 4 === b ? 0 : 1;
    let solved = 0;
    while (!SHAPES[shape].every((d) => [a, b].includes((d + solved) % 4))) solved++;
    cells[index] = {
      shape,
      rotation: (solved + 1 + (((seed ^ Math.imul(index + 1, level * 1664525)) >>> 0) % 3)) % 4,
      active: true,
    };
  }
  const fire = level > 1 ? path[Math.floor(path.length / 2)] : -1;
  return {
    seed,
    level,
    cells,
    source: path[0],
    bucket: path.at(-1),
    fire,
    gate: level > 1 ? path.at(-2) : -1,
    moves: 0,
    attempts: 0,
    complete: false,
    result: null,
    score: 0,
  };
}
export function waterPorts(cell) {
  return SHAPES[cell.shape].map((d) => (d + cell.rotation) % 4);
}
/** Rotate a single existing channel clockwise. Won levels remain held. */
export function rotateWaterworks(state, index) {
  if (!Number.isInteger(index) || index < 0 || index >= 25)
    throw new RangeError('Choose a channel.');
  if (state.complete || !state.cells[index].active) return state;
  return {
    ...state,
    cells: state.cells.map((c, i) => (i === index ? { ...c, rotation: (c.rotation + 1) % 4 } : c)),
    moves: state.moves + 1,
    result: null,
  };
}
/** Release exactly 100 water units through reciprocal connected channels.
 * A leak loses all arriving water; no board mutation occurs during animation.
 * Steam consumes exactly 12 water units, opens the downstream gate, and is
 * counted separately from delivered water. No water or points are duplicated. */
export function releaseWaterworks(state) {
  if (state.complete) return state;
  let index = state.source,
    inlet = 0,
    water = 100,
    steam = 0,
    leaked = 0,
    complete = false;
  const route = [],
    visited = new Set();
  for (let step = 0; step < 25; step++) {
    const cell = state.cells[index],
      ports = waterPorts(cell);
    if (!cell.active || !ports.includes(inlet) || visited.has(index)) {
      leaked = water;
      water = 0;
      break;
    }
    route.push(index);
    visited.add(index);
    if (index === state.fire) {
      water -= 12;
      steam += 12;
    }
    if (index === state.gate && steam < 12) {
      leaked = water;
      water = 0;
      break;
    }
    const exit = ports.find((d) => d !== inlet);
    if (index === state.bucket && exit === 2) {
      complete = true;
      break;
    }
    const [dx, dy] = DIRECTIONS[exit],
      x = (index % 5) + dx,
      y = Math.floor(index / 5) + dy;
    if (y < Math.floor(index / 5) || x < 0 || x >= 5 || y >= 5) {
      leaked = water;
      water = 0;
      break;
    }
    index = y * 5 + x;
    inlet = (exit + 2) % 4;
  }
  if (!complete && water) {
    leaked += water;
    water = 0;
  }
  const result = { route, delivered: complete ? water : 0, steam, leaked, complete };
  return {
    ...state,
    attempts: state.attempts + 1,
    complete,
    result,
    score: complete ? water * 20 + Math.max(0, 600 - state.moves * 10) + state.level * 250 : 0,
  };
}
/** Derive visible channels and conservation feedback, without a solution witness. */
export function waterworksView(state) {
  return {
    cells: state.cells.map((cell, index) => ({
      ...cell,
      index,
      ports: waterPorts(cell),
      fire: index === state.fire,
      gate: index === state.gate,
    })),
    goal:
      state.fire >= 0
        ? 'Quench the fire. Steam opens the gate. Deliver 88 drops.'
        : 'Connect the spring to the bucket. Save all 100 drops.',
    result: state.result,
  };
}
